import { Box, Button, Textarea } from '@chakra-ui/react';
import { Plus } from 'iconoir-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface NoteComposerProps {
  /**
   * Called with the trimmed draft when the user confirms. The composer clears its
   * own field; the caller is responsible for persisting the note.
   */
  onAdd?: (text: string) => void;
}

/**
 * Note input: an outline Textarea plus a secondary add button. Owns its draft
 * locally (ephemeral UI state, not server state).
 */
const NoteComposer: React.FC<NoteComposerProps> = ({ onAdd }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');

  const handleAdd = () => {
    const text = draft.trim();
    if (!text) return;
    onAdd?.(text);
    setDraft('');
  };

  return (
    <Box>
      <Textarea
        variant="outline"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={t('contactDrawer.notes.placeholder')}
        rounded="10px"
        minH="64px"
        bg="panelBg"
        fontFamily="body"
        fontSize="12.5px"
        lineHeight="1.5"
      />
      <Button
        onClick={handleAdd}
        disabled={!draft.trim()}
        w="full"
        h="36px"
        mt="8px"
        gap="6px"
        rounded="md"
        bg="panelBg"
        borderWidth="1px"
        borderColor="border.emphasized"
        color="primary.700"
        fontFamily="heading"
        fontSize="12.5px"
        fontWeight="medium"
        _hover={{ bg: 'whiteSmoke' }}
      >
        <Plus width={15} height={15} />
        {t('contactDrawer.notes.add')}
      </Button>
    </Box>
  );
};

export default NoteComposer;
